import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { CompanyStatus } from '../../models/business/CompanyStatus'
import { companyStatusSchema } from '../../models/schema'
import {
  createCompanyStatus as createCompanyStatusAPI,
  deleteCompanyStatus as deleteCompanyStatusAPI,
  fetchCompanyStatusById,
  fetchCompanyStatuses,
  updateCompanyStatus as updateCompanyStatusAPI,
} from '../../services/business/CompanyStatusService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<CompanyStatus> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'companyStatus',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCompanyStatusesSuccess,
  setQuery: setCompanyStatusQuery,
  reset: resetCompanyStatusState,
  resetQueryAndIds: resetCompanyStatusQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: CompanyStatus }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCompanyStatuses = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCompanyStatus = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCompanyStatusById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCompanyStatusesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCompanyStatus =
  (companyStatus: CompanyStatus | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<CompanyStatus>(companyStatus))
    )

export const getCompanyStatuses = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCompanyStatuses(params),
    [companyStatusSchema],
    slice.actions
  )

export const createCompanyStatus = (companyStatus: CompanyStatus) =>
  SBAPI.CreateDispatched(
    createCompanyStatusAPI(companyStatus),
    companyStatusSchema,
    slice.actions
  )

export const getCompanyStatusWithId = (id: number) =>
  SBAPI.FetchDispatched<CompanyStatus>(
    fetchCompanyStatusById(id),
    companyStatusSchema,
    slice.actions
  )

export const updateCompanyStatus = (companyStatus: CompanyStatus) =>
  SBAPI.UpdateDispatched<CompanyStatus>(
    updateCompanyStatusAPI(companyStatus),
    companyStatus,
    slice.actions
  )

export const deleteCompanyStatus = (companyStatus: CompanyStatus) =>
  SBAPI.DestroyDispatched<CompanyStatus>(
    deleteCompanyStatusAPI(companyStatus),
    companyStatus,
    slice.actions
  )
